import i18n from '@/config/i18n.config'
import { useLoadingStore } from '@/modules/core/stores/loadingStore'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useCommonStore } from './commonStore'
import { useToastStore } from './toastStore'

import HealthInsurance from '@/modules/health/common/models/APP/HealthInsurance'
import HealthHiringAnswers from '@/modules/health/hiring/models/APP/HealthHiringAnswers'
import HealthRaterAnswers from '@/modules/health/rater/models/APP/HealthRaterAnswers'
import { isBigger } from '@/utils/date.utils'
// import HealthHiringAnswersExtended from '@/modules/health/hiring/models/APP/HealthHiringAnswersExtended'
import Insurance from '@/modules/common/common/models/APP/Insurance'
import type BankDetails from '@/modules/common/hiring/models/APP/BankDetails'
import HealthMapper from '@/modules/core/mappers/HealthMapper'
import CustomError from '@/modules/core/models/errors/CustomError'
import ServiceError from '@/modules/core/models/errors/ServiceError'
import ModalButton from '@/modules/core/models/modalMessage/ModalButton'
import DXPService from '@/modules/core/services/DXPService'
import MiddlewareService from '@/modules/core/services/MiddlewareService'
import HealthConfigurations from '@/modules/health/common/models/APP/HealthConfigurations'
import AdditionalCoverageConfig from '@/modules/health/hiring/models/APP/AdditionalCoverageConfig'
import HealthHiringInsured from '@/modules/health/hiring/models/APP/HealthHiringInsured'
import HealthInsuranceProcessStepEnum from '@/modules/health/hiring/models/APP/HealthInsuranceProcessStepEnum'
import type HealthLandingInsured from '@/modules/health/hiring/models/APP/HealthLandingInsured'
import { servicesMock } from '../../../mocks/serviceMockData'
import { useErrorManagerComposable } from '../composables/errorManagerComposable'
import ENDPOINTSAPI from '../constants/apiEndpoints'
import ENDPOINTSMOCK from '../constants/apiEndpoints.mocks'
import { ErrorDisplayTypeEnum, InsuranceProcessStatusEnum, UserRoleEnum } from '../models/enums'
import type { OptionsValuesInterface } from '../models/types'
import { checkRecoveredQuotationBranch } from '../utils/BranchUtils'

export const useHealthStore = defineStore('health', () => {
  const errorManager = useErrorManagerComposable()
  const toastStore = useToastStore()
  const loadingStore = useLoadingStore()
  const commonStore = useCommonStore()
  const router = useRouter()
  const { t } = i18n.global
  const isLoading = ref(false) // to show loader during the call

  const globalConfig = ref<HealthConfigurations | null>(null)
  const raterFormAnswers = ref(new HealthRaterAnswers())

  const hiringFormAnswers = ref(new HealthHiringAnswers())
  const additionalCoveragesConfig = ref<AdditionalCoverageConfig[]>([])
  const insuredsState = computed(() => {
    const insureds = hiringFormAnswers.value.insureds
    const insuredsComplete = insureds.map(
      (i) =>
        hiringFormAnswers.value.currentInsured?.riskNumber! > i.riskNumber &&
        (!!i.documentDetails.documentNumber || i.documentDetails.documentType === 'MENOR')
    )
    const holder = hiringFormAnswers.value.holder.role
    const index = holder === UserRoleEnum.HOLDER_NOT_INSURED ? 0 : 1

    return insuredsComplete.slice(index, insureds.length)
  })

  const stepsBeforeSaveAndExit = computed(() => {
    const stepCode = hiringFormAnswers.value.processStatus?.stepCode ?? 0
    const role = hiringFormAnswers.value.holder.role
    const insuranceStep = {
      label: t('copy_save-and-exit-modal_step-insurance'),
      check: true
    }
    const documentStep = {
      label: t('copy_save-and-exit-modal_step-document'),
      check: stepCode > HealthInsuranceProcessStepEnum.DOCUMENTS
    }

    const holderInsuredIndex = !insuredsState.value.length ? null : 1
    const holderStep = {
      label: t(`copy_save-and-exit-modal_step-holder-${role}`, { n: holderInsuredIndex }),
      check: stepCode > HealthInsuranceProcessStepEnum.HOLDER
    }

    const firstInsuredIndex = role === UserRoleEnum.HOLDER_INSURED ? 1 : 0
    const insuredsSteps = insuredsState.value.map((check, index) => ({
      label: t('copy_save-and-exit-modal_step-insured', { n: ++index + firstInsuredIndex }),
      check: check
    }))

    const questionnaireStep = {
      label: t('copy_save-and-exit-modal_step-questionnaire'),
      check: stepCode > HealthInsuranceProcessStepEnum.QUESTIONNAIRES
    }

    return [insuranceStep, documentStep, holderStep, ...insuredsSteps, questionnaireStep]
  })

  const ENDPOINTS = !loadingStore.isActiveMock()
    ? { ...ENDPOINTSAPI }
    : { ...ENDPOINTSAPI, ...ENDPOINTSMOCK }
  const mock = loadingStore.isActiveMock()

  function getBranchConfiguration() {
    return globalConfig.value
  }

  function setHiringFormAnswers(
    answers: Partial<HealthHiringAnswers | HealthHiringInsured>,
    insuredIndex?: number
  ) {
    if (!answers) return
    if (insuredIndex === undefined) {
      hiringFormAnswers.value.updateAttributes(answers)
    } else if (insuredIndex === 0) {
      hiringFormAnswers.value.updateHolderAttributes(answers)
    } else {
      hiringFormAnswers.value.updateInsuredAttributes(insuredIndex, answers)
    }
  }

  function getHiringFormAnswers(insuredIndex?: number): HealthHiringAnswers {
    const currentInsured = _getCurrentInsured(insuredIndex)
    const answers = hiringFormAnswers.value as HealthHiringAnswers
    const insured = currentInsured as HealthHiringInsured
    answers.currentInsured = insured ? new HealthHiringInsured(insured) : null

    return answers
  }

  function _getCurrentInsured(insuredIndex?: number | string) {
    if (insuredIndex === undefined) return null

    if (insuredIndex === 0 || insuredIndex === 'holder') return hiringFormAnswers.value.holder

    if (hiringFormAnswers.value.holder.role === UserRoleEnum.HOLDER_NOT_INSURED) {
      return hiringFormAnswers.value.insureds[(insuredIndex as number) - 1]
    }

    return hiringFormAnswers.value.insureds[insuredIndex as number]
  }

  function getAdditionalCoveragesConfig() {
    return additionalCoveragesConfig.value
  }

  //** PRIVATE SUPPORT METHODS */
  async function _fetchDXPInsurances(
    ids: { productCode: string; clientCode: string; coverageCode?: string }[]
  ) {
    const endpoint = ENDPOINTS.DXP_GET_PRODUCT
    const request = {
      language: 'es',
      products: ids
    }
    const { error, data } = await DXPService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    return data.map((d: any) => d.product)
  }

  async function _getProposals(middlewareData: any[]): Promise<HealthInsurance[]> {
    const ids = middlewareData.map((i: any) => ({
      productCode: String(i.productCode),
      clientCode: i.clientCode
    }))
    if (ids.length === 0) {
      throw new Error(t('copy_rater-empty-error-modal_title')) // ??
    }

    const dxpData = await _fetchDXPInsurances(ids)

    const products: HealthInsurance[] = []
    dxpData.forEach((DXPProduct: any) => {
      const MIDProduct = middlewareData.find(
        (p) => String(DXPProduct.productId) === String(p.productCode)
      )

      if (MIDProduct) {
        const productData = { ...DXPProduct, ...MIDProduct }
        products.push(HealthMapper.fromModifiedRaterResponse(productData))
      }
    })

    return products
  }

  // ✅✅
  async function loadConfiguration(reload = false) {
    try {
      if (globalConfig.value?.businessLineId && !reload) {
        raterFormAnswers.value.businessLine = globalConfig.value.businessLineId
        return
      }
      const { error, data } = await DXPService.get(ENDPOINTS.DXP_GET_BRANCH_HEALTH)
      if (error) {
        throw new ServiceError(data).setDisplayType(ErrorDisplayTypeEnum.MODAL)
      }

      globalConfig.value = new HealthConfigurations({
        ...data,
        defaultHiringMaxAges: data.hiringMaxAges
      })
      raterFormAnswers.value.businessLine = globalConfig.value.businessLineId
    } catch (error) {
      loadingStore.stopLoading()
      errorManager.handleError(error)
    }
  }

  async function _saveQuotation(
    quotationId: string | null,
    startDate: Date,
    billingCycle: string,
    product: Insurance,
    holderRole: UserRoleEnum
  ) {
    if (!quotationId) throw new Error('Quotation id is required')

    const endpoint = ENDPOINTS.SAVE_QUOTATION
    const request = HealthMapper.toSaveQuotation(
      quotationId,
      startDate,
      billingCycle,
      product,
      holderRole
    )
    const config = { mock: mock && servicesMock.SAVE_QUOTATION }
    const { error, data } = await MiddlewareService.post(endpoint, request, config)
    if (error) throw new ServiceError(data)
  }

  // ✅
  async function hireProduct(
    quotationId: string | null,
    startDate: Date,
    billingCycle: string,
    product: Insurance,
    holderRole: UserRoleEnum
  ) {
    let valid = true

    try {
      loadingStore.startLoading('common.loading.processing')
      await _saveQuotation(quotationId, startDate, billingCycle, product, holderRole)
    } catch (error) {
      errorManager.handleError(error)
      valid = false
    } finally {
      loadingStore.stopLoading()
    }

    return { valid }
  }

  // *********** HIRING ***********
  // ✅✅ call to get new rater of specific product -> 1 proposal
  async function modifyRate(
    answers: HealthRaterAnswers,
    errorDisplayType: ErrorDisplayTypeEnum = ErrorDisplayTypeEnum.TOAST,
    isQuote: boolean = false
  ): Promise<HealthInsurance | null> {
    try {
      loadingStore.startLoading('common.loading.processing')

      const endpoint = ENDPOINTS.RATER_CALCULATE_PRICE
      const request = HealthMapper.toReRaterRequest(answers, isQuote)
      const config = { mock: mock && servicesMock.RATER_CALCULATE_PRICE }
      const { error, data } = await MiddlewareService.post(endpoint, request, config)
      if (error) throw new ServiceError(data)

      const products = data.listPricing
      const insuranceProposals = await _getProposals(products)

      return insuranceProposals[0]
    } catch (error) {
      if (error instanceof CustomError) {
        error.setDisplayType(errorDisplayType)
      }
      errorManager.handleError(error)

      return null
    } finally {
      loadingStore.stopLoading()
    }
  }

  // ✅ Nuevo precio de la retarificacion con coverturas adicionales
  async function getNewRaterPrice(
    answers: HealthHiringAnswers,
    errorDisplayType: ErrorDisplayTypeEnum = ErrorDisplayTypeEnum.TOAST,
    productCode: string | null | undefined = undefined,
    isQuoted: boolean = false
  ): Promise<HealthInsurance | null> {
    try {
      loadingStore.startLoading('common.loading.processing')

      const dataToTransform = new HealthRaterAnswers(answers)
      const endpoint = ENDPOINTS.RATER_CALCULATE_PRICE
      const request = HealthMapper.toProductRaterRequest(dataToTransform, isQuoted)
      const config = { mock: mock && servicesMock.RATER_CALCULATE_PRICE }
      const { error, data } = await MiddlewareService.post(endpoint, request, config)
      if (error) throw new ServiceError(data)

      const products = data.listPricing
      const insuranceProposals = await _getProposals(products)

      return (
        (productCode
          ? insuranceProposals.find((i) => i.productCode === productCode)
          : insuranceProposals[0]) ?? null
      )
    } catch (error) {
      if (error instanceof CustomError) {
        error.setDisplayType(errorDisplayType)
      }
      errorManager.handleError(error)

      return null
    } finally {
      loadingStore.stopLoading()
    }
  }

  async function fetchHiringFormAnswers(quotationId: string) {
    try {
      commonStore.showLogo = false
      const endpoint = `${ENDPOINTS.GET_RECOVERY}?quotationId=${quotationId}`
      const config = { mock: mock && servicesMock.GET_RECOVERY }
      const { error, data } = await MiddlewareService.get(endpoint, config)
      if (error) {
        throw new ServiceError(data).setDisplayType(ErrorDisplayTypeEnum.MODAL).setButtons([
          new ModalButton({
            text: 'copy_button_emission_exit',
            navTo: 'exit'
          })
        ])
      }
      await commonStore.resolveMediatorFromRecovery(data.mediator?.mediator)
      await loadConfiguration(true)
      const branchConfig = getBranchConfiguration()
      const itsOk = await checkRecoveredQuotationBranch(
        data.hiring.businessLine,
        branchConfig!.businessLineId,
        quotationId
      )
      if (!itsOk) return
      const coverageCode =
        data.insureds[0].coverages.find((c: any) =>
          //TODO: this should not be hardcoded
          ['GS51', 'GS52', 'GS53'].includes(c.coverageCode)
        )?.coverageCode ?? null
      const dxpData = await _fetchDXPInsurances([
        {
          productCode: data.hiring.productCode,
          clientCode: data.hiring.clientCode,
          coverageCode
        }
      ])
      if (!dxpData.length) {
        throw new ServiceError(data).setDisplayType(ErrorDisplayTypeEnum.MODAL).setButtons([
          new ModalButton({
            text: 'copy_button_emission_exit',
            navTo: 'exit'
          })
        ])
      }
      const DXPProduct = dxpData[0]
      const insurance = HealthMapper.fromModifiedRaterResponse(DXPProduct)

      if (DXPProduct.hiringMaxAge && globalConfig.value && globalConfig.value.hiringMaxAges) {
        globalConfig.value.hiringMaxAges.holderInsured.max = DXPProduct.hiringMaxAge
        globalConfig.value.hiringMaxAges.insured.max = DXPProduct.hiringMaxAge
      }

      if (
        data.hiring.stayAbroadType?.toString() === globalConfig.value?.stayAbroadTypes?.study.id
      ) {
        globalConfig.value!.hiringMaxAges = globalConfig.value!.hiringMaxAgesInternationalStudy
      }
      if (data.hiring.stayAbroadType?.toString() === globalConfig.value?.stayAbroadTypes?.work.id) {
        globalConfig.value!.hiringMaxAges = globalConfig.value!.hiringMaxAgesInternationalWork
      }
      // If insurance type is international, tags are overwritten
      if (data.hiring.insuranceTypes.includes(3)) {
        globalConfig.value!.tags.anotherInsuranceAlert =
          globalConfig.value!.tags.anotherInternationalInsuranceAlert
        globalConfig.value!.tags.anotherInsuranceNotHolderAlert =
          globalConfig.value!.tags.anotherInternationalInsuranceNotHolderAlert
      }
      hiringFormAnswers.value = HealthMapper.fromRecoverHiringData(
        { ...data, coverageCode },
        insurance
      )
    } catch (error) {
      errorManager.handleError(error)
      loadingStore.stopLoading()
    } finally {
      commonStore.showLogo = true
    }
  }

  const availableHiringMethods = {
    sendPolicyInsuredDocument,
    sendPolicyHolderDocument: calculateSendDocumentMethod,
    checkExistClient,
    sendPolicyHolder,
    sendInsureds,
    sendQuestionnaire,
    sendPayment
  }
  async function callToHiringMethod(
    methodName: keyof typeof availableHiringMethods,
    errorDisplayType: ErrorDisplayTypeEnum = ErrorDisplayTypeEnum.TOAST,
    answers: HealthHiringAnswers
  ): Promise<void> {
    try {
      loadingStore.startLoading('common.loading.processing')

      if (methodName && methodName in availableHiringMethods) {
        await availableHiringMethods[methodName](answers)
      }
    } catch (error) {
      const err = error instanceof CustomError ? error.setDisplayType(errorDisplayType) : error
      errorManager.handleError(err)
      throw error
    }
  }
  function _setStepProcess(stepProcess: HealthInsuranceProcessStepEnum) {
    const currentStepCode = hiringFormAnswers.value.processStatus.stepCode ?? 0
    if (currentStepCode < stepProcess) {
      hiringFormAnswers.value.processStatus.stepCode = stepProcess
    }
  }
  function calculateSendDocumentMethod(answers: HealthHiringAnswers) {
    if (answers.currentInsured?.role !== UserRoleEnum.INSURED) {
      sendPolicyHolderDocument(answers)
    } else {
      sendPolicyInsuredDocument(answers)
    }
  }
  async function sendPolicyHolderDocument(answers: HealthHiringAnswers) {
    const endpoint = ENDPOINTS.SAVE_DOCUMENT
    const quotationId = answers.quotationId ?? ''
    const holder = answers.holder as HealthHiringInsured
    const request = await HealthMapper.toHolderDocumentRequest(quotationId, holder)
    const config = { mock: mock && servicesMock?.SAVE_DOCUMENT }
    const { error, data } = await MiddlewareService.post(endpoint, request, config)
    if (error) throw new ServiceError(data)

    _setStepProcess(HealthInsuranceProcessStepEnum.HOLDER)
    const product = answers.product as HealthInsurance
    if (product?.insuranceType === '3') {
      toastStore.successToast('health.saved-holder-data-and-documents')
    } else {
      toastStore.successToast('health.saved-holder-data-and-document')
    }
  }

  async function sendPolicyInsuredDocument(answers: HealthHiringAnswers) {
    const endpoint = ENDPOINTS.SAVE_DOCUMENT_INSURED
    const quotationId = answers.quotationId ?? ''
    const insured = answers.currentInsured as HealthHiringInsured
    const request = await HealthMapper.toInsuredDocumentRequest(quotationId, insured)
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    const documentType =
      answers.currentInsured?.extraDocument?.documentType === '3'
        ? t('health.proof-of-work')
        : t('health.proof-of-studies')

    toastStore.successToast(
      t('health.saved-insured-document', {
        document: documentType,
        riskNumber: answers.currentInsured?.riskNumber
      })
    )
  }

  // TODO: Pending to remove
  async function checkExistClient(answers: HealthHiringAnswers) {
    // use in common (confirmado que se usara en todas las ramas)
    const holder = answers.holder
    const endpoint = ENDPOINTS.MDW_HEALTH_EXIST_CLIENT
    const request = HealthMapper.toExistClientRequest(holder)
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    holder.updateAttributes({ thirdPartiesAnswered: data.hasDataPD && data.clientExists })
    setHiringFormAnswers({ holder })
  }

  async function sendPolicyHolder(answers: HealthHiringAnswers) {
    const endpoint = ENDPOINTS.SAVE_HOLDER
    const quotationId = answers.quotationId ?? ''
    const holder = answers.holder as HealthHiringInsured
    const request = HealthMapper.toHolderRequest(quotationId, holder)
    const config = { mock: mock && servicesMock?.SAVE_HOLDER }
    const { error, data } = await MiddlewareService.post(endpoint, request, config)
    if (error) throw new ServiceError(data)

    const onlyInsureHolder =
      answers.insureds.length === 1 && answers.holder.role === UserRoleEnum.HOLDER_INSURED
    const processStep = onlyInsureHolder
      ? HealthInsuranceProcessStepEnum.QUESTIONNAIRES
      : HealthInsuranceProcessStepEnum.INSUREDS

    _setStepProcess(processStep)

    toastStore.successToast(
      answers.holder.role === UserRoleEnum.HOLDER_INSURED
        ? 'health.saved-holder-and-others-data'
        : 'health.saved-holder-data'
    )
  }

  async function sendInsureds(answers: HealthHiringAnswers) {
    const endpoint = ENDPOINTS.SAVE_INSUREDS
    const quotationId = answers.quotationId ?? ''

    if (answers.currentInsured?.role !== UserRoleEnum.INSURED) return

    const request = HealthMapper.toInsuredsRequest(quotationId, [answers.currentInsured])
    const config = { mock: mock && servicesMock?.SAVE_INSUREDS }
    const { error, data } = await MiddlewareService.post(endpoint, request, config)
    if (error) throw new ServiceError(data)

    const isLastInsured =
      answers.insureds[answers.insureds.length - 1].riskNumber ===
      answers.currentInsured?.riskNumber
    if (isLastInsured) {
      _setStepProcess(HealthInsuranceProcessStepEnum.QUESTIONNAIRES)
    }
    toastStore.successToast(
      t('health.saved-insured-data', { riskNumber: answers.currentInsured?.riskNumber })
    )
  }

  async function sendQuestionnaire(answers: HealthHiringAnswers) {
    const endpoint = ENDPOINTS.SAVE_QUESTIONNAIRE
    const quotationId = answers.quotationId ?? ''
    const insureds = answers.insureds as HealthHiringInsured[]
    const request = HealthMapper.toQuestionnaireRequest(quotationId, insureds)
    const config = { mock: mock && servicesMock?.SAVE_QUESTIONNAIRE }
    const { error, data } = await MiddlewareService.post(endpoint, request, config)
    if (error) throw new ServiceError(data)

    _setStepProcess(HealthInsuranceProcessStepEnum.PAYMENT)
    toastStore.successToast('health.saved-all-data')
  }

  async function sendPayment(answers: HealthHiringAnswers) {
    const quotationId = answers.quotationId ?? ''
    const bankDetails = answers.bankDetails as BankDetails

    const { error, data } = await commonStore.sendPayment(quotationId, bankDetails)
    if (error) throw new ServiceError(data)

    _setStepProcess(HealthInsuranceProcessStepEnum.SIGN)
    //toastStore.successToast('health.saved-bank-data')
  }

  async function _fetchDXPCoverages(middlewareData: any[]) {
    const endpoint = ENDPOINTS.DXP_GET_COVERAGES
    const coverages = middlewareData.map((c) => ({
      coverageCode: c.coverageCode
    }))
    const request = { language: 'es', coverages: coverages }
    const { error, data } = await DXPService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    return data
  }

  function _loadCompleteSheetCoverages(middlewareData: any[], dxpData: any[]) {
    const coverages: AdditionalCoverageConfig[] = []
    dxpData.forEach(({ coverage: DXPCoverage }) => {
      const middlewareCoverage = middlewareData.find(
        (p) => String(DXPCoverage.coverageId) === String(p.coverageCode)
      )

      if (middlewareCoverage) {
        const coverageData = { ...DXPCoverage, ...middlewareCoverage }
        coverages.push(HealthMapper.fromAdditionalCoveragesConfig(coverageData))
      }
    })

    return coverages
  }

  async function fetchAdditionalCoveragesConfig(quotationId: string) {
    try {
      const endpoint = `${ENDPOINTS.MDW_GET_COVERAGES}?quotationId=${quotationId}`
      const config = { mock: mock && servicesMock.MDW_GET_COVERAGES }
      const { error, data } = await MiddlewareService.get(endpoint, config)
      if (error) {
        throw new ServiceError(data).setDisplayType(ErrorDisplayTypeEnum.MODAL).setButtons([
          new ModalButton({
            text: 'copy_button_emission_exit',
            navTo: 'exit'
          })
        ])
      }

      const middlewareData = data.coverageInfoData
      const dxpData = await _fetchDXPCoverages(middlewareData)
      additionalCoveragesConfig.value = _loadCompleteSheetCoverages(middlewareData, dxpData)
    } catch (error) {
      errorManager.handleError(error)
    }
  }

  function _checkAdditionalCoverageEligibility(insuredBirthday: Date) {
    if (!insuredBirthday) return true

    if (!additionalCoveragesConfig.value.length) return false

    return additionalCoveragesConfig.value.some((c) => {
      const older = isBigger(insuredBirthday, c.minAge)
      const younger = !isBigger(insuredBirthday, c.maxAge)

      return older && younger
    })
  }

  function canPurchaseAdditionalCoverages() {
    return hiringFormAnswers.value.insureds.some((i) => {
      return _checkAdditionalCoverageEligibility(i.userDetails.birthDate as Date)
    })
  }

  function isProcessExpired() {
    // return isBigger(hiringFormAnswers.value.startDate, 0)
    const statusCode = hiringFormAnswers.value.processStatus.statusCode
    return statusCode === InsuranceProcessStatusEnum.EXPIRED
  }

  async function validateLanding(
    quotationId: string,
    validationDate: string
  ): Promise<HealthLandingInsured[]> {
    try {
      const endpoint = ENDPOINTS.MDW_VALIDATE_LANDING
      const request = { quotationId, validationDate }
      const config = { mock: mock && servicesMock.MDW_VALIDATE_LANDING }
      const { error, data } = await MiddlewareService.post(endpoint, request, config)
      if (error) throw new ServiceError(data)

      return HealthMapper.fromLandingResponse(data)
    } catch (error) {
      errorManager.handleError(error)

      return []
    }
  }

  async function fetchLandingEntities(): Promise<OptionsValuesInterface[]> {
    try {
      isLoading.value = true

      const endpoint = ENDPOINTS.DXP_GET_ENTITIES
      const { error, data } = await DXPService.get(endpoint)
      if (error) throw new ServiceError(data)

      const entities = data['sisnet entities']
      return Object.entries(entities)
        .map(([value, label]) => ({ value, label: String(label) }))
        .sort((a, b) => a.label.localeCompare(b.label))
    } catch (error) {
      errorManager.handleError(error)

      return []
    }
  }

  async function sendDeficiencyPeriods(insureds: HealthLandingInsured[]) {
    try {
      loadingStore.startLoading('common.loading.processing')

      const endpoint = ENDPOINTS.MDW_SEND_LANDING
      const request = await HealthMapper.toLandingRequest(insureds)
      const config = { mock: mock && servicesMock.REMOVE_PERIODS }
      const { error, data } = await MiddlewareService.post(endpoint, request, config)
      if (error) throw new ServiceError(data)

      toastStore.successToast('health.removed-grace-period')
      router.push({ name: 'rater-nav' })
    } catch (error) {
      errorManager.handleError(error)
    }
  }

  async function emission(quotationId: string) {
    return await commonStore.fetchInsuranceState(
      quotationId,
      'hiring-health',
      ENDPOINTS.POLICY_EMISSION
    )
  }

  function getBranchName(): string {
    return 'health'
  }

  return {
    getBranchConfiguration,
    loadConfiguration,

    //* RATER
    hireProduct,

    //* HIRING
    getAdditionalCoveragesConfig,
    modifyRate,
    setHiringFormAnswers,
    getHiringFormAnswers,
    stepsBeforeSaveAndExit,
    callToHiringMethod,
    fetchHiringFormAnswers,
    fetchAdditionalCoveragesConfig,
    getNewRaterPrice,
    canPurchaseAdditionalCoverages,
    isProcessExpired,
    validateLanding,
    fetchLandingEntities,
    sendDeficiencyPeriods,
    emission,
    getBranchName
  }
})
